import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import { wholesaleService } from '../../services/wholesaleService';

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const response = await wholesaleService.getOrder(orderId);
      if (!response.order) {
        throw new Error('Order not found');
      }
      setOrder(response.order);
    } catch (error) {
      setError('Failed to fetch order details');
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      await wholesaleService.getOrderInvoice(orderId);
    } catch (error) {
      setError('Failed to download invoice');
      console.error('Error downloading invoice:', error);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
          <button 
            className="btn btn-outline-primary mt-3 d-block"
            onClick={() => navigate('/wholesale/orders')}
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning" role="alert">
          Order not found
          <button 
            className="btn btn-outline-primary mt-3 d-block"
            onClick={() => navigate('/wholesale/orders')}
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Order #{order.orderID}</h2>
        <button 
          className="btn btn-outline-secondary"
          onClick={() => navigate('/wholesale/orders')}
        >
          Back to Orders
        </button>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <h5 className="card-title">Order Information</h5>
              <p><strong>Date:</strong> {new Date(order.orderDate).toLocaleDateString()}</p>
              <p><strong>Status:</strong> 
                <span className={`badge ms-2 ${
                  order.orderStatus === 'completed' ? 'bg-success' :
                  order.orderStatus === 'processing' ? 'bg-primary' :
                  'bg-secondary'
                }`}>
                  {order.orderStatus}
                </span>
              </p>
              <p><strong>Payment Due:</strong> {new Date(order.paymentDueDate).toLocaleDateString()}</p>
            </div>
            <div className="col-md-6">
              <h5 className="card-title">Shipping Information</h5>
              <p>{order.shippingAddress}</p>
              <p>{order.shippingCity}, {order.shippingState} {order.shippingZip}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Order Items</h5>
          <button 
            className="btn btn-outline-primary btn-sm"
            onClick={handleDownloadInvoice}
          >
            <Download size={16} className="me-2" />
            Download Invoice
          </button>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-center">Quantity</th>
                  <th className="text-end">Price</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map(item => (
                  <tr key={item.orderItemID}>
                    <td>
                      <div>{item.name}</div>
                      <small className="text-muted">
                        {item.vintage} {item.varietal}
                      </small>
                    </td>
                    <td className="text-center">{item.quantity} cases</td>
                    <td className="text-end">${Number(item.pricePerUnit).toFixed(2)}</td>
                    <td className="text-end">${Number(item.totalPrice).toFixed(2)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="3" className="text-end"><strong>Total Amount</strong></td>
                  <td className="text-end"><strong>${Number(order.totalAmount).toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;